import { createElement, useLayoutEffect, useRef } from "react";

import { SectionRole } from "../../shared/types/roles";
import { truncate } from "../../shared/utils";

import { ChatMentionToast } from "./ChatMentionToast";

import { SecondaryOverlay } from "components/constants";
import type { ChatMessageType } from "components/server-types";
import { ChatWithLinesIcon } from "mds/icons";
import { store, useAppDispatch, useAppSelector } from "store";
import {
  filterChatMentionTargetsForAuthor,
  getChatMentionAuthorPermissions,
  selectChatMentionAuthorPermissionsByUserId,
  selectChatMentionTargetsForCurrentUser,
  selectCurrentClassUser,
  selectCurrentSectionId,
  selectCurrentSectionRole,
  selectCurrentUserId,
  userSelectors,
} from "store/selectors";
import { selectIsObservingVirtualClass } from "store/selectors/pubsub-virtual-class-state";
import { chatMentionsActions } from "store/slices/chat-mentions";
import { toastInfoMessage } from "utils/alerts";
import { BROADCAST_TOPICS, useBroadcastListener } from "utils/broadcast";
import { rollbarAndLogError } from "utils/logger";
import { useOverlayControls } from "utils/routing/hooks/use-overlay-controls";
import { getUserFullName } from "utils/user-utils";

export const useChatMentionObserver = () => {
  const pendingMentionMessageIds = useRef(new Set<string>());
  const dispatch = useAppDispatch();
  const sectionId = useAppSelector(selectCurrentSectionId);
  const currentSectionRole = useAppSelector(selectCurrentSectionRole);
  const currentUserIsInClassSession = useAppSelector(
    (state) => selectCurrentClassUser(state)?.is_in_class_session ?? false,
  );
  const currentUserIsObservingClass = useAppSelector(selectIsObservingVirtualClass);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentUserMentionTargets = useAppSelector(selectChatMentionTargetsForCurrentUser);
  const { isSelected: isChatOpen, togglePanel: toggleChatPanel } = useOverlayControls(
    SecondaryOverlay.ClassChat,
  );
  const chatControlsRef = useRef({ isChatOpen, toggleChatPanel });

  useLayoutEffect(() => {
    chatControlsRef.current = { isChatOpen, toggleChatPanel };
  }, [isChatOpen, toggleChatPanel]);

  useBroadcastListener(
    BROADCAST_TOPICS.PUBSUB_MODEL_UPDATE,
    (message) => {
      if (message.type !== "model-update" || message.model !== "chat_messages") {
        return;
      }

      const chatMessage = message.data as Pick<ChatMessageType, "id">;
      if (!chatMessage.id) return;

      if (message.action === "delete") {
        pendingMentionMessageIds.current.delete(chatMessage.id);
        dispatch(
          chatMentionsActions.removeDeletedMention({
            messageId: chatMessage.id,
            sectionId: message.section_id,
          }),
        );
        return;
      }
      if (message.action !== "create") return;

      const createdChatMessage = message.data as ChatMessageType;
      if (
        createdChatMessage.section_id !== sectionId ||
        createdChatMessage.author_id === currentUserId ||
        (!currentUserIsInClassSession && !currentUserIsObservingClass) ||
        currentSectionRole === SectionRole.RECORDING_AGENT
      ) {
        return;
      }

      const state = store.getState();
      const authorPermissions = getChatMentionAuthorPermissions(
        selectChatMentionAuthorPermissionsByUserId(state),
        createdChatMessage.author_id,
      );
      const author = userSelectors.selectById(state, createdChatMessage.author_id);
      const eligibleMentionTexts = filterChatMentionTargetsForAuthor(
        currentUserMentionTargets,
        authorPermissions,
      ).map((target) => target.mentionText);
      if (!eligibleMentionTexts.length) return;

      pendingMentionMessageIds.current.add(createdChatMessage.id);
      import("./markdown-mentions")
        .then(({ findVisibleMentionsInMarkdown }) => {
          if (!pendingMentionMessageIds.current.delete(createdChatMessage.id)) return;

          const visibleMentions = findVisibleMentionsInMarkdown(
            createdChatMessage.text,
            eligibleMentionTexts,
          );
          if (!visibleMentions.length) return;

          dispatch(
            chatMentionsActions.addUnseenMention({
              messageId: createdChatMessage.id,
              sectionId: createdChatMessage.section_id,
            }),
          );

          if (chatControlsRef.current.isChatOpen) return;

          const openMention = () => {
            dispatch(
              chatMentionsActions.requestOldestUnseenMention({
                sectionId: createdChatMessage.section_id,
              }),
            );
            if (!chatControlsRef.current.isChatOpen) {
              chatControlsRef.current.toggleChatPanel();
            }
          };

          toastInfoMessage(
            createElement(ChatMentionToast, {
              authorName: author ? getUserFullName(author) : null,
              messagePreview: truncate(createdChatMessage.text, 120),
              onClick: openMention,
            }),
            { icon: () => createElement(ChatWithLinesIcon), role: "status" },
          );
        })
        .catch((error) => {
          pendingMentionMessageIds.current.delete(createdChatMessage.id);
          rollbarAndLogError("Loading chat mention parser failed", error);
        });
    },
    [
      currentSectionRole,
      currentUserIsInClassSession,
      currentUserIsObservingClass,
      currentUserId,
      currentUserMentionTargets,
      dispatch,
      sectionId,
    ],
  );
};
